import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Box, Link as MuiLink, Typography } from "@mui/material";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import useNavigateToLeaseComp from "../../hooks/useNavigateToLeaseComp";
import useNavigateToSaleComp from "../../hooks/useNavigateToSaleComp";
import { ICompComparable, IEstimateCompsInfo } from "../../hooks/usePropertyEstimates";
import ILeaseComp from "../../interfaces/ILeaseComp";
import ISaleComp from "../../interfaces/ISaleComp";
import { formatNumber, formatPrice } from "../../utils/utils";

export const MonthlyRentalComparableCompsInfo = observer(function MonthlyRentalComparableCompsInfo({ data }: { data: IEstimateCompsInfo<ILeaseComp> }) {
    return (
        <CompsInfoSection
            data={data}
            orderBy={i => i.derived?.monthlyRentalRate ?? 0}
            navigate={useNavigateToLeaseComp()}
            cells={(c) => [
                { label: "Building Size", value: formatNumberInner(c.comp.buildingSize) },
                { label: "Tenant Land Size", value: formatNumberInner(c.comp.tenantLandSize) },
                { label: "Distance", value: formatMilesInner(c.distance) },
                { label: "Monthly Rental Rate", value: formatPriceInner(c.derived?.monthlyRentalRate) },
            ]}
        />
    );
});

export const SalePricePerAcreComparableCompsInfo = observer(function SalePricePerAcreComparableCompsInfo({ data }: { data: IEstimateCompsInfo<ISaleComp> }) {
    return (
        <CompsInfoSection
            data={data}
            orderBy={i => i.comp.pricePerUsableAcre!}
            navigate={useNavigateToSaleComp()}
            cells={(c) => [
                { label: "Building Size", value: formatNumberInner(c.comp.buildingSize) },
                { label: "Usable Land Size", value: formatNumberInner(c.comp.landSizeUsable) },
                { label: "Distance", value: formatMilesInner(c.distance) },
                { label: "Sale $ Per Usable Acre", value: formatPriceInner(c.comp.pricePerUsableAcre) },
            ]}
        />
    );
});

export const SalePricePerSqFtComparableCompsInfo = observer(function SalePricePerSqFtComparableCompsInfo({ data }: { data: IEstimateCompsInfo<ISaleComp> }) {
    return (
        <CompsInfoSection
            data={data}
            orderBy={i => i.comp.buildingPerSqFt!}
            navigate={useNavigateToSaleComp()}
            cells={(c) => [
                { label: "Building Size", value: formatNumberInner(c.comp.buildingSize) },
                { label: "Usable Land Size", value: formatNumberInner(c.comp.landSizeUsable) },
                { label: "Distance", value: formatMilesInner(c.distance) },
                { label: "Sale $ Per Sq-Ft", value: formatPriceInner(c.comp.buildingPerSqFt) },
            ]}
        />
    );
});

function InfoAccordion({ children, disabled }: { children: React.ReactNode; disabled?: boolean }) {
    const [open, setOpen] = useState(false);
    if (disabled) return null;

    return (
        <Accordion disableGutters expanded={open} onChange={(_, v) => setOpen(v)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx}>
                <Typography sx={sumTextSx}>Details</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1 }}>{children}</AccordionDetails>
        </Accordion>
    );
}

function CardBox({ children, highlight }: { children: React.ReactNode; highlight?: boolean }) {
    return (
        <Box sx={{
            border: "1px solid",
            borderColor: highlight ? "black" : "divider",
            borderRadius: 1,
            p: 0.75,
            boxShadow: highlight ? 2 : 0
        }}>
            {children}
        </Box>
    );
}

function GridLike({ children }: { children: React.ReactNode }) {
    return (
        <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr 1fr" }}
            columnGap={0.75}
            rowGap={0.75}
            marginTop={"10px"}
            sx={{
                "& > div": {
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0.75,
                    p: 0.75
                }
            }}
        >
            {children}
        </Box>
    );
}

function Cell({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <Box>
            <Typography sx={{ fontSize: "10px" }} color="text.secondary">
                {label}
            </Typography>
            <Typography sx={{ fontSize: "10px" }}>{value}</Typography>
        </Box>
    );
}

function CompsInfoSection<T>({ data, orderBy, navigate, cells }: ComparableCompsInfoBaseProps<T>) {
    const items = useMemo(() => _.orderBy(data.comps, orderBy, "desc"), [data.comps, orderBy]);

    if (!items.length) 
        return null;

    return (
        <InfoAccordion>
            <Box display="grid" gap={0.75}>
                {items.map((c) => {
                    return (
                        <CardBox key={(c.comp as any).id} highlight={data.winner?.comp === c.comp}>
                            <MuiLink
                                component="button"
                                onClick={() => navigate((c.comp as any).id)}
                                sx={titleSx}
                            >
                                {(c.comp as any).address}
                            </MuiLink>
                            <GridLike>
                                {cells(c).map(({ label, value }) => (
                                    <Cell key={label} label={label} value={value} />
                                ))}
                            </GridLike>
                        </CardBox>
                    );
                })}
            </Box>
        </InfoAccordion>
    );
}

interface ComparableCompsInfoBaseProps<T> {
    data: IEstimateCompsInfo<T>;
    orderBy: (item: ICompComparable<T>) => number;
    navigate: (id: number) => void;
    cells: (item: ICompComparable<T>) => { label: string; value: React.ReactNode }[];
}

const sumSx = {
    minHeight: 36,
    "&.Mui-expanded": { minHeight: 36 },
    "& .MuiAccordionSummary-content": {
        border: "none", my: 0, width: "100%",
        display: "flex", justifyContent: "flex-end"
    },
    px: 1
} as const;

const sumTextSx = { fontSize: "12px", fontWeight: 600, color: "var(--text-color)" } as const;

const titleSx = {
    fontSize: "10px",
    fontWeight: 600,
    textAlign: "left",
    color: "var(--text-color)",
    "&:hover": { color: "var(--text-color)", textDecoration: "underline" }
} as const;

const formatNumberInner = (v?: number | null) => (v == null ? "—" : formatNumber(v));
const formatMilesInner = (m?: number | null) => (m == null ? "—" : `${formatNumber(m)} miles`);
const formatPriceInner = (v?: number | null) => (v == null ? "—" : formatPrice(v));
